import React, { useMemo } from "react";
import cx from "classnames";

import { Outcome } from "./Outcome";
import { ExpandableOutcomes } from "./ExpandableOutcomes";
import { LiftedTeamNames } from "./LiftedTeamNames";
import { getOutcomeTeamCompetitorId } from "@/utilities/sharedBettingUtilities";
import { useIsLiveViewOnly } from "@/features/betting/hooks/useIsLiveViewOnly";
import {
  sortPlayerOutcomes,
  groupByPlayer,
  hasCleanHomeAwaySplit,
  type PlayerMarketLayoutProps,
} from "./playerMarketLayout";

import type { ProcessedOutcome } from "@/hooks/firestore/betting/useEvent";

import * as styles from "./HomeAwayColumns.module.scss";

const INITIAL_COLUMN_ROWS = 3;

const withPlayerName = (
  outcome: ProcessedOutcome,
  playerName?: string,
): ProcessedOutcome => {
  const label = outcome.abbreviation ?? outcome.name ?? "";
  if (!playerName || !label || label.includes(playerName)) return outcome;

  return {
    ...outcome,
    abbreviation: undefined,
    name: `${playerName} ${label}`,
  };
};

const splitByPlayer = (outcomes: ProcessedOutcome[]): ProcessedOutcome[][] => {
  const columns: ProcessedOutcome[][] = [[], []];

  groupByPlayer(outcomes).forEach((group) => {
    const target =
      columns[0].length <= columns[1].length ? columns[0] : columns[1];
    target.push(...group);
  });

  return columns;
};

// splits sorted outcomes into two display columns. Home/away when the split
// is clean; a team filter leaving only one side interleaves it into two
// "columns" so the shared grid still wraps 2-per-row instead of one lopsided
// column sitting next to a blank other half; otherwise falls back to a
// generic split by player
const toColumns = (
  sorted: ProcessedOutcome[],
  isCleanHomeAwaySplit: boolean,
): ProcessedOutcome[][] => {
  if (!isCleanHomeAwaySplit) {
    return splitByPlayer(sorted).filter((column) => column.length > 0);
  }

  const home = sorted.filter((o) => o.type?.endsWith("_HOME"));
  const away = sorted.filter((o) => o.type?.endsWith("_AWAY"));
  const sides = [home, away].filter((side) => side.length > 0);
  if (sides.length !== 1) return sides;

  const [side] = sides;
  return [
    side.filter((_, index) => index % 2 === 0),
    side.filter((_, index) => index % 2 === 1),
  ];
};

// two columns: home on the left when the outcomes name a side, otherwise
// players split across the two by count.
const HomeAwayColumns = ({
  outcomes,
  competitors,
  eventId,
  eventStatus,
  oddsFormat,
  selections,
  toggleSelection,
  isLoading = false,
  mainMarket,
  renderFieldsKey,
  forceExpand,
}: PlayerMarketLayoutProps) => {
  const isLiveViewOnly = useIsLiveViewOnly(eventStatus);

  const { visible, hidden, hiddenCount, isCleanHomeAwaySplit } = useMemo(() => {
    const sorted = sortPlayerOutcomes(
      outcomes,
      renderFieldsKey,
      isLiveViewOnly,
    ).map((outcome) => {
      const competitorId = outcome.actualCompetitorId ?? outcome.competitorId;
      return withPlayerName(
        outcome,
        competitorId ? competitors[competitorId]?.name : undefined,
      );
    });

    // only use the home/away split when it covers every outcome - otherwise
    // an extra outcome (e.g. a draw) gets silently dropped, so fall back to
    // the generic split
    const isCleanHomeAwaySplit = hasCleanHomeAwaySplit(outcomes);
    const columns = toColumns(sorted, isCleanHomeAwaySplit);

    return {
      visible: columns.map((column) => column.slice(0, INITIAL_COLUMN_ROWS)),
      hidden: columns.map((column) => column.slice(INITIAL_COLUMN_ROWS)),
      hiddenCount: columns.reduce(
        (count, column) =>
          count + Math.max(column.length - INITIAL_COLUMN_ROWS, 0),
        0,
      ),
      isCleanHomeAwaySplit,
    };
  }, [outcomes, competitors, renderFieldsKey, isLiveViewOnly]);

  const renderCell = (outcome: ProcessedOutcome, dividesRight: boolean) => {
    const competitorId = outcome.actualCompetitorId ?? outcome.competitorId;
    const player = competitorId ? competitors[competitorId] : null;
    // team logo, falling back to the player when the franchise can't be resolved
    const teamId = getOutcomeTeamCompetitorId(outcome, mainMarket);
    const team = teamId ? competitors[teamId] : null;

    return (
      <Outcome
        key={outcome.id}
        id={outcome.id}
        variant="row"
        className={cx(styles.cell, { [styles.divider]: dividesRight })}
        eventId={eventId}
        eventStatus={eventStatus}
        marketId={outcome.market?.id}
        outcome={outcome}
        competitor={team ?? player}
        toggleSelection={toggleSelection}
        isSuspended={!outcome.active}
        isSelected={selections.includes(outcome.id)}
        oddsFormat={oddsFormat}
        status={outcome.market?.status}
        isLoading={isLoading}
        renderFieldsKey={renderFieldsKey}
      />
    );
  };

  // cells share one grid, so a wrapped name grows both sides. A spacer holds
  // a short column and carries the divider to the bottom
  const renderColumns = (blocks: ProcessedOutcome[][]) => {
    const rowCount = Math.max(...blocks.map((block) => block.length), 0);

    return (
      <div
        className={cx(styles.columns, { [styles.single]: blocks.length === 1 })}
      >
        {Array.from({ length: rowCount }, (_, row) =>
          blocks.map((block, column) => {
            const outcome = block[row];
            // everything but the last column draws the split to its right
            const dividesRight = column < blocks.length - 1;

            return outcome ? (
              renderCell(outcome, dividesRight)
            ) : (
              <div
                key={`spacer-${column}-${row}`}
                className={cx({ [styles.divider]: dividesRight })}
              />
            );
          }),
        )}
      </div>
    );
  };

  return (
    <>
      {isCleanHomeAwaySplit && (
        <LiftedTeamNames
          outcomes={outcomes}
          competitors={competitors}
          mainMarket={mainMarket}
          showLogos={false}
        />
      )}

      <ExpandableOutcomes
        className={cx(styles.container, {
          // the pill above already delineates the header - drop the grid's
          // own top border so it doesn't read as sitting on the pill
          [styles.noHeaderBorder]: isCleanHomeAwaySplit,
        })}
        hiddenCount={hiddenCount}
        collapsed={hiddenCount > 0 ? renderColumns(hidden) : null}
        forceExpand={forceExpand}
      >
        {renderColumns(visible)}
      </ExpandableOutcomes>
    </>
  );
};

export { HomeAwayColumns };
